import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui';
import { Icon } from '../../icons';

export default function CalendarView({ amenities = [], bookings = [], onBookSlot }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAmenity, setSelectedAmenity] = useState(amenities[0]?.id || "");

  useEffect(() => {
    if (amenities && amenities.length > 0) {
      if (!selectedAmenity || !amenities.some(a => String(a.id) === String(selectedAmenity))) {
        setSelectedAmenity(amenities[0].id);
      }
    } else {
      setSelectedAmenity("");
    }
  }, [amenities, selectedAmenity]);

  const amenity = amenities.find(a => String(a.id) === String(selectedAmenity)) || (amenities.length > 0 ? amenities[0] : null);
  const currentAmenityId = amenity?.id || selectedAmenity;

  const dayBookings = bookings.filter(b => b.date === selectedDate && String(b.amenity_id) === String(currentAmenityId) && b.booking_status !== 'cancelled');

  const startHour = amenity?.opening_time ? parseInt(amenity.opening_time.split(':')[0]) : 6;
  const endHour = amenity?.closing_time ? parseInt(amenity.closing_time.split(':')[0]) : 22;
  const slotDuration = amenity?.slot_duration_minutes || 60;

  const slots = [];
  let currentMinutes = startHour * 60;
  const endMinutes = endHour * 60;

  while (currentMinutes < endMinutes) {
    const slotStartStr = `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`;
    const nextMinutes = currentMinutes + slotDuration;
    if (nextMinutes > endMinutes) break;
    const slotEndStr = `${String(Math.floor(nextMinutes / 60)).padStart(2, '0')}:${String(nextMinutes % 60).padStart(2, '0')}`;
    
    slots.push({
      start: slotStartStr,
      end: slotEndStr,
      startMin: currentMinutes,
      endMin: nextMinutes,
      label: `${slotStartStr} - ${slotEndStr}`
    });
    
    currentMinutes = nextMinutes;
  }

  return (
    <div className="space-y-6 fade-up">
      {/* Date & Amenity Selector Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
        {/* Date Selector */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 shadow-sm hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
          <label className="block text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">
            Select Date
          </label>
          <div className="relative flex items-center">
            <span className="text-slate-400 shrink-0 pointer-events-none">
              <Icon name="calendar" size={16} />
            </span>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={e => setSelectedDate(e.target.value)} 
              className="bg-transparent border-none text-slate-800 dark:text-slate-200 font-semibold text-sm outline-none w-full pl-2.5 cursor-pointer focus:ring-0" 
            />
          </div>
        </div>

        {/* Amenity Dropdown */}
        <div className={`bg-white dark:bg-slate-900 border rounded-xl p-3 shadow-sm transition-colors ${amenities.length === 0 ? 'border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}>
          <label className="block text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">
            Select Amenity
          </label>
          <div className="relative flex items-center">
            <span className="text-slate-400 shrink-0 pointer-events-none">
              <Icon name="map_pin" size={16} />
            </span>
            <select
              value={currentAmenityId}
              onChange={e => setSelectedAmenity(e.target.value)}
              disabled={amenities.length === 0}
              className="bg-transparent border-none text-slate-800 dark:text-slate-200 font-semibold text-sm outline-none w-full pl-2.5 pr-8 cursor-pointer appearance-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {amenities.length === 0 ? (
                <option value="" disabled className="bg-white text-slate-500 dark:bg-slate-900 font-normal">
                  No amenities available
                </option>
              ) : (
                amenities.map(a => (
                  <option key={a.id} value={a.id} className="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 font-medium">
                    {a.name}
                  </option>
                ))
              )}
            </select>
            <span className="text-slate-400 pointer-events-none absolute right-1">
              <Icon name="chevron_down" size={14} />
            </span>
          </div>
        </div>
      </div>
      
      {amenity ? (
        <Card className="bg-slate-50 dark:bg-slate-900/30 border-none shadow-none">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
             {slots.map((slot, i) => {
               const bookingMatches = dayBookings.filter(b => {
                 if (!b.start_time || !b.end_time) return false;
                 const bStartMin = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
                 const bEndMin = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
                 return Math.max(slot.startMin, bStartMin) < Math.min(slot.endMin, bEndMin);
               });
               const isBooked = bookingMatches.length > 0;
               return (
                 <div 
                   key={i} 
                   onClick={() => !isBooked && onBookSlot(amenity.id, selectedDate, slot.start, slot.end)}
                   className={`group relative p-4 rounded-xl border transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-sm ${isBooked ? 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:shadow-md cursor-pointer'}`}
                 >
                   <div className="flex items-center justify-between mb-3">
                     <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{slotDuration} Min Slot</span>
                     {isBooked ? (
                       <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">Booked</span>
                     ) : (
                       <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 group-hover:bg-emerald-500 group-hover:text-white transition-colors">Available</span>
                     )}
                   </div>
                   
                   <div className={`text-lg font-semibold tracking-tight ${isBooked ? 'text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-100 group-hover:text-primary transition-colors'}`}>
                     {slot.label}
                   </div>

                   {isBooked && (
                     <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/60 flex items-center gap-2">
                       <Icon name="user" size={12} className="text-slate-400" />
                       <span className="text-xs font-medium text-slate-500">{bookingMatches[0].booked_by}</span>
                     </div>
                   )}
                 </div>
               )
             })}
          </div>
        </Card>
      ) : (
        <div className="text-center py-12 px-4 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 shadow-sm">
            <Icon name="calendar" size={22} />
          </div>
          <div className="text-slate-700 dark:text-slate-300 font-semibold text-sm">
            {amenities.length === 0 ? "No Amenities Added Yet" : "Select an amenity to view the daily schedule"}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 max-w-xs mx-auto">
            {amenities.length === 0 
              ? "Add amenities from the Amenities tab to view and manage daily booking slots."
              : "Choose an amenity from the dropdown above to view its availability."}
          </p>
        </div>
      )}
    </div>
  );
}
